import React, { useState, useEffect, useRef, useCallback } from 'react';

const API = process.env.REACT_APP_API_URL || window.__APP_API_BASE__ || 'http://localhost:4000';

// Mapa de tipo de documento según el rol del usuario
function getTipo(role) {
    const r = (role || '').toUpperCase();
    if (r === 'OWNER')   return 'propietario';
    if (r === 'CLIENT')  return 'cliente';
    if (['CAMARERA','SUPERVISORA','MANTENIMIENTO','STAFF','EDITOR_PROPS','EDITOR_EXPS','VENDEDOR_INTERNO'].includes(r)) return 'empleado';
    if (['ADMIN','SUPER_ADMIN'].includes(r)) return 'admin';
    return null;
}

export default function LegalDocsModal({ auth, trabAuth, onDone }) {
    const [docs, setDocs]           = useState([]);   // documentos pendientes
    const [current, setCurrent]     = useState(0);    // índice actual
    const [scrolled, setScrolled]   = useState(false); // llegó al final
    const [loading, setLoading]     = useState(true);
    const [accepting, setAccepting] = useState(false);
    const [accepted, setAccepted]   = useState([]);   // ya aceptados en esta sesión
    const scrollRef = useRef(null);

    // Determinar entityId, entityType y tipo según el perfil
    const getInfo = useCallback(() => {
        if (trabAuth?.id) {
            return {
                entityId:   `empleado:${trabAuth.id}`,
                entityType: 'empleado',
                tipo:       'empleado',
            };
        }
        if (auth?.clienteId) {
            const tipo = getTipo(auth.role);
            if (!tipo) return null;
            return {
                entityId:   `user:${auth.clienteId}`,
                entityType: 'user',
                tipo,
            };
        }
        return null;
    }, [auth, trabAuth]);

    useEffect(() => {
        const info = getInfo();
        if (!info) { onDone?.(); return; }

        fetch(`${API}/api/legal/check?entityId=${encodeURIComponent(info.entityId)}&tipo=${info.tipo}`)
            .then(r => r.ok ? r.json() : { pendientes: [] })
            .then(d => {
                const pendientes = d.pendientes || [];
                if (pendientes.length === 0) { onDone?.(); return; }
                setDocs(pendientes);
                setLoading(false);
            })
            .catch(() => { onDone?.(); });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleScroll = (e) => {
        const el = e.target;
        const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
        if (nearBottom) setScrolled(true);
    };

    const handleAccept = async () => {
        const doc = docs[current];
        const info = getInfo();
        if (!doc || !info || accepting) return;

        setAccepting(true);
        try {
            const res = await fetch(`${API}/api/legal/accept`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    entityId:    info.entityId,
                    entityType:  info.entityType,
                    documentId:  doc.id,
                    version:     doc.version,
                    contentHash: doc.contentHash,
                    userAgent:   navigator.userAgent,
                }),
            });

            if (res.ok) {
                const newAccepted = [...accepted, doc.id];
                setAccepted(newAccepted);
                const next = current + 1;
                if (next >= docs.length) {
                    // Todos aceptados
                    onDone?.();
                } else {
                    setCurrent(next);
                    setScrolled(false);
                    if (scrollRef.current) scrollRef.current.scrollTop = 0;
                }
            }
        } catch { /* si falla la red, dejamos intentar de nuevo */ }
        finally { setAccepting(false); }
    };

    if (loading) return null;
    if (docs.length === 0) return null;

    const doc = docs[current];
    const totalDocs = docs.length;

    return (
        <div style={{
            position: 'fixed', inset: 0, zIndex: 99999,
            background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '16px',
        }}>
            <div style={{
                background: '#fff', borderRadius: 18, width: '100%', maxWidth: 680,
                maxHeight: '90vh', display: 'flex', flexDirection: 'column',
                boxShadow: '0 24px 64px rgba(0,0,0,0.4)',
            }}>
                {/* Cabecera */}
                <div style={{
                    padding: '20px 24px 16px', borderBottom: '1px solid #e2e8f0',
                    background: '#003366', borderRadius: '18px 18px 0 0',
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <span style={{ fontSize: 22 }}>📋</span>
                            <div>
                                <h2 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: '#fff' }}>
                                    Documento requerido
                                </h2>
                                <p style={{ margin: 0, fontSize: 12, color: 'rgba(255,255,255,0.7)' }}>
                                    Debes leer y aceptar para continuar
                                </p>
                            </div>
                        </div>
                        {totalDocs > 1 && (
                            <span style={{
                                background: 'rgba(255,255,255,0.2)', color: '#fff',
                                borderRadius: 20, padding: '4px 12px', fontSize: 12, fontWeight: 700,
                            }}>
                                {current + 1} / {totalDocs}
                            </span>
                        )}
                    </div>

                    <div style={{ marginTop: 14 }}>
                        <h3 style={{ margin: '0 0 2px', fontSize: 18, fontWeight: 800, color: '#fff' }}>
                            {doc.nombre}
                        </h3>
                        <span style={{
                            fontSize: 11, color: 'rgba(255,255,255,0.6)', fontWeight: 600,
                            textTransform: 'uppercase', letterSpacing: 1,
                        }}>
                            Versión {doc.version}
                        </span>
                    </div>

                    {/* Barra de progreso */}
                    {totalDocs > 1 && (
                        <div style={{ marginTop: 12, height: 4, background: 'rgba(255,255,255,0.2)', borderRadius: 4 }}>
                            <div style={{
                                height: '100%', borderRadius: 4, background: '#22c55e',
                                width: `${((current) / totalDocs) * 100}%`,
                                transition: 'width 0.4s',
                            }} />
                        </div>
                    )}
                </div>

                {/* Contenido del documento */}
                <div
                    ref={scrollRef}
                    onScroll={handleScroll}
                    style={{
                        flex: 1, overflowY: 'auto', padding: '20px 24px',
                        fontSize: 14, lineHeight: 1.7, color: '#334155',
                    }}
                    dangerouslySetInnerHTML={{ __html: doc.contenido }}
                />

                {/* Pie — acción */}
                <div style={{ padding: '16px 24px 20px', borderTop: '1px solid #e2e8f0', background: '#f8fafc', borderRadius: '0 0 18px 18px' }}>
                    {!scrolled && (
                        <p style={{ margin: '0 0 10px', fontSize: 12, color: '#94a3b8', textAlign: 'center', fontWeight: 600 }}>
                            Desplázate hasta el final del documento para poder aceptarlo
                        </p>
                    )}

                    <label style={{
                        display: 'flex', alignItems: 'flex-start', gap: 10,
                        cursor: scrolled ? 'pointer' : 'default',
                        opacity: scrolled ? 1 : 0.45,
                        marginBottom: 14,
                    }}>
                        <input
                            type="checkbox"
                            disabled={!scrolled}
                            checked={scrolled}
                            readOnly
                            style={{ marginTop: 2, width: 16, height: 16, accentColor: '#003366' }}
                        />
                        <span style={{ fontSize: 13, color: '#1e293b', fontWeight: 600 }}>
                            He leído y entendido el documento "{doc.nombre}" (v{doc.version}) y acepto sus términos
                        </span>
                    </label>

                    <button
                        onClick={handleAccept}
                        disabled={!scrolled || accepting}
                        style={{
                            width: '100%', padding: '14px',
                            background: scrolled && !accepting ? '#003366' : '#94a3b8',
                            color: '#fff', border: 'none', borderRadius: 12,
                            fontWeight: 800, fontSize: 15,
                            cursor: scrolled && !accepting ? 'pointer' : 'not-allowed',
                            transition: 'background 0.2s',
                        }}
                    >
                        {accepting ? 'Registrando firma...' : current + 1 < totalDocs ? `Acepto — Continuar al siguiente documento` : 'Acepto — Ingresar al portal'}
                    </button>

                    <p style={{ margin: '10px 0 0', fontSize: 11, color: '#94a3b8', textAlign: 'center' }}>
                        Tu aceptación queda registrada con fecha, hora e IP como firma digital válida
                    </p>
                </div>
            </div>
        </div>
    );
}
